import React, { useEffect, useRef, useState } from 'react';
import { AVATAR_PALETTE } from '../constants/avatarPalette';
import './avatar.scss';

export interface AvatarColorEditorLabels {
  color1: string;
  color2: string;
  pickColor: string;
}

interface AvatarProps {
  matrix: number[][];
  color1: string;
  color2: string;
  compact?: boolean;
  circular?: boolean;
}

const Avatar: React.FC<AvatarProps> = ({
  matrix,
  color1,
  color2,
  compact = false,
  circular = false,
}) => {
  if (!matrix.length) return null;
  const mods = [
    'avatar',
    compact ? 'avatar--compact' : '',
    circular ? 'avatar--circular' : '',
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <div className={mods} aria-hidden>
      {matrix.map((row, rowIndex) => (
        <div className="avatar__row" key={`row-${rowIndex}`}>
          {row.map((pixel, columnIndex) => (
            <span
              className="avatar__pixel"
              key={`pixel-${rowIndex}-${columnIndex}`}
              style={{
                backgroundColor: pixel === 1 ? color1 : color2,
              }}
            />
          ))}
        </div>
      ))}
    </div>
  );
};

interface AvatarColorEditorProps {
  color1: string;
  color2: string;
  labels: AvatarColorEditorLabels;
  onColor1Change: (hex: string) => void;
  onColor2Change: (hex: string) => void;
}

export const AvatarColorEditor: React.FC<AvatarColorEditorProps> = ({
  color1,
  color2,
  labels,
  onColor1Change,
  onColor2Change,
}) => {
  const [openSlot, setOpenSlot] = useState<null | 1 | 2>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (openSlot === null) return;
    const close = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpenSlot(null);
      }
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [openSlot]);

  const apply = (slot: 1 | 2, hex: string) => {
    if (slot === 1) onColor1Change(hex);
    else onColor2Change(hex);
    setOpenSlot(null);
  };

  return (
    <div className="avatar-color-editor" ref={wrapRef}>
      <div className="avatar-color-editor__slot">
        <span className="avatar-color-editor__label">{labels.color1}</span>
        <div className="avatar-color-editor__swatch-wrap">
          <button
            type="button"
            className="avatar-color-editor__swatch"
            style={{ backgroundColor: color1 }}
            aria-expanded={openSlot === 1}
            aria-haspopup="listbox"
            aria-label={labels.color1}
            onClick={() => setOpenSlot((s) => (s === 1 ? null : 1))}
          />
          {openSlot === 1 && (
            <div className="avatar-color-editor__popover" role="listbox">
              {AVATAR_PALETTE.map((hex) => (
                <button
                  key={hex}
                  type="button"
                  className="avatar-color-editor__popover-cell"
                  style={{ backgroundColor: hex }}
                  aria-label={`${labels.pickColor} ${hex}`}
                  onClick={() => apply(1, hex)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
      <div className="avatar-color-editor__slot">
        <span className="avatar-color-editor__label">{labels.color2}</span>
        <div className="avatar-color-editor__swatch-wrap">
          <button
            type="button"
            className="avatar-color-editor__swatch"
            style={{ backgroundColor: color2 }}
            aria-expanded={openSlot === 2}
            aria-haspopup="listbox"
            aria-label={labels.color2}
            onClick={() => setOpenSlot((s) => (s === 2 ? null : 2))}
          />
          {openSlot === 2 && (
            <div className="avatar-color-editor__popover" role="listbox">
              {AVATAR_PALETTE.map((hex) => (
                <button
                  key={hex}
                  type="button"
                  className="avatar-color-editor__popover-cell"
                  style={{ backgroundColor: hex }}
                  aria-label={`${labels.pickColor} ${hex}`}
                  onClick={() => apply(2, hex)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Avatar;
